/**
 * Full Recording Preview Modal - Sprint 20+
 * Plays all scenes sequentially with transition effects for complete recording preview
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Typography, Space, Button, Dropdown, Tooltip } from 'antd';
import { Play, Pause, RotateCcw, X, Maximize, Zap, Wind, Blend, MoveRight } from 'lucide-react';
import { Scene } from '../../../../types/scene';
import { db } from '../../idb/chunk-db';
import { SceneService } from '../../services/sceneService';
import { VideoThumbnailService } from '../../services/videoThumbnailService';
import logger from '../../utils/logger';
import './FullRecordingPreviewModal.scss';

const { Text, Title } = Typography;

interface FullRecordingPreviewModalProps {
  visible: boolean;
  scenes: Scene[];
  onClose: () => void;
  cinemaMode?: boolean;
  projectId?: string;
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const FullRecordingPreviewModal: React.FC<FullRecordingPreviewModalProps> = ({
  visible,
  scenes,
  onClose,
  cinemaMode = false,
  projectId,
}) => {
  const [isPlaying, setIsPlaying] = useState(false); // Start paused, will auto-play after loading
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [sceneVideos, setSceneVideos] = useState<Record<string, string>>({});
  const [sceneThumbnails, setSceneThumbnails] = useState<Record<string, string>>({}); // Enhancement #5: Real thumbnails
  const [isLoading, setIsLoading] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // Playback speed: 0.5x, 1x, 1.5x, 2x
  const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen mode
  const [hoverTime, setHoverTime] = useState<number | null>(null); // For timeline hover preview
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  
  // Transition state
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const [nextSceneIndex, setNextSceneIndex] = useState<number | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const nextVideoRef = useRef<HTMLVideoElement>(null); // For transition preview
  const animationFrameRef = useRef<number | null>(null);
  const sceneStartTimeRef = useRef<number>(0); // Track when current scene started playing
  const progressBarRef = useRef<HTMLDivElement>(null); // For hover interactions
  const modalContainerRef = useRef<HTMLDivElement>(null); // For fullscreen
  const transitionStartTimeRef = useRef<number>(0); // For transition animation

  // Load all scene videos when modal opens
  useEffect(() => {
    if (!visible) {
      return;
    }

    const loadSceneVideos = async () => {
      setIsLoading(true);
      const videoUrls: Record<string, string> = {};
      const thumbnailUrls: Record<string, string> = {};
      
      try {
        // CRITICAL FIX: Reload scenes from database to get fresh videoChunks data
        // The scenes from props may have stale/empty videoChunks due to race condition
        // Pass projectId so getSceneById can fetch from cloud with proper transitionOut
        const freshScenes: Scene[] = [];
        for (const scene of scenes) {
          const freshScene = await SceneService.getSceneById(scene.id, projectId);
          if (freshScene) {
            // Merge transitionOut from original scene prop as fallback (in case freshScene doesn't have it)
            freshScenes.push({
              ...freshScene,
              transitionOut: freshScene.transitionOut || scene.transitionOut,
            });
          }
        }

        const recordedScenes = freshScenes.filter(s => s.status === 'recorded' && s.videoChunks && s.videoChunks.length > 0);
        
        for (const scene of recordedScenes) {
          try {

            // Get chunks for this scene
            const chunks = await db.chunks
              .where('id')
              .anyOf(scene.videoChunks)
              .sortBy('chunkNo');
            
            logger.info(`[FullRecordingPreview] Chunks retrieved for ${scene.title}:`, {
              chunksFound: chunks.length,
              chunkDetails: chunks.map(c => ({ id: c.id, chunkNo: c.chunkNo, size: c.size }))
            });

            if (chunks.length > 0) {
              // Combine chunks into a single blob
              const blobs = chunks.map(c => c.blob);
              const mimeType = chunks[0].blob.type || 'video/webm';
              const combinedBlob = new Blob(blobs, { type: mimeType });
              const videoUrl = URL.createObjectURL(combinedBlob);
              videoUrls[scene.id] = videoUrl;
              
              // Enhancement #5: Extract thumbnail from video
              try {
                const thumbnailResult = await VideoThumbnailService.extractFrameFromVideo(
                  combinedBlob,
                  'first',
                  { width: 40, height: 24, quality: 0.7 }
                );
                thumbnailUrls[scene.id] = thumbnailResult.dataUrl;
                logger.info(`[FullRecordingPreview] Thumbnail extracted for ${scene.title}`);
              } catch (thumbError) {
                logger.warn(`[FullRecordingPreview] Failed to extract thumbnail for ${scene.title}:`, thumbError);
              }
              
              logger.info(`[FullRecordingPreview] Loaded video for scene: ${scene.title}`, {
                chunks: chunks.length,
                size: combinedBlob.size,
                mimeType,
                videoUrl
              });
            } else {
              logger.warn(`[FullRecordingPreview] No chunks found for scene: ${scene.title}`, {
                sceneId: scene.id,
                expectedChunks: scene.videoChunks
              });
            }
          } catch (error) {
            logger.error(`[FullRecordingPreview] Error loading scene ${scene.id}:`, error);
          }
        }
        
        setSceneVideos(videoUrls);
        setSceneThumbnails(thumbnailUrls);
        
        // Calculate total duration (scene.duration is in milliseconds)
        // Subtract transition overlaps - crossfade/slide/fade transitions cause scenes to overlap
        const totalSceneDuration = recordedScenes.reduce((sum, scene) => sum + scene.duration, 0);
        let transitionOverlapMs = 0;
        
        recordedScenes.forEach((scene, index) => {
          // Subtract transition overlap for all scenes except the last
          if (index < recordedScenes.length - 1 && scene.transitionOut) {
            const transitionType = scene.transitionOut.type || 'cut';
            // Only crossfade, slide, and fade transitions cause overlap
            if (transitionType === 'crossfade' || transitionType === 'slide' || transitionType === 'fade') {
              const transitionDurationSec = scene.transitionOut.duration || 0;
              transitionOverlapMs += transitionDurationSec * 1000;
            }
          }
        });
        
        const total = Math.max(0, totalSceneDuration - transitionOverlapMs) / 1000;
        setTotalDuration(total);
        
        logger.info('[FullRecordingPreview] Loading complete:', {
          videosLoaded: Object.keys(videoUrls).length,
          thumbnailsLoaded: Object.keys(thumbnailUrls).length,
          totalDurationSeconds: total
        });
        
      } catch (error) {
        logger.error('[FullRecordingPreview] Error loading videos:', error);
      } finally {
        setIsLoading(false);
        // Auto-play after videos finish loading
        setIsPlaying(true);
      }
    };

    loadSceneVideos();

    // Cleanup URLs when modal closes
    return () => {
      Object.values(sceneVideos).forEach(url => URL.revokeObjectURL(url));
      // Note: thumbnail dataUrls don't need revoking (they're base64)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, scenes, projectId]); // Intentionally excluding sceneVideos to avoid infinite loop

  // Complete transition and move to next scene
  const completeTransition = useCallback(() => {
    setCurrentSceneIndex(prev => prev + 1);
    setIsTransitioning(false);
    setTransitionProgress(0);
    setNextSceneIndex(null);
  }, []);

  // Handle scene ended - START TRANSITION to next scene
  const handleVideoEnded = useCallback(() => {
    // Don't trigger if already transitioning
    if (isTransitioning) {
      return;
    }
    
    if (currentSceneIndex < scenes.length - 1) {
      const currentScene = scenes[currentSceneIndex];
      const transitionType = currentScene.transitionOut?.type || 'cut';
      const transitionDuration = currentScene.transitionOut?.duration || 0;
      
      // Start transition
      setNextSceneIndex(currentSceneIndex + 1);
      setIsTransitioning(true);
      setTransitionProgress(0);
      transitionStartTimeRef.current = performance.now();
      
      // If it's a cut (instant), complete immediately
      if (transitionType === 'cut' || transitionDuration === 0) {
        setTimeout(() => {
          completeTransition();
        }, 100); // Small delay for visual feedback
      }
    } else {
      // Finished all scenes
      setIsPlaying(false);
      setCurrentSceneIndex(0);
    }
  }, [currentSceneIndex, scenes, isTransitioning, completeTransition]);

  // Handle video playback
  useEffect(() => {
    if (!isPlaying || !videoRef.current || isLoading) {
      return;
    }

    const playCurrentScene = async () => {
      const currentScene = scenes[currentSceneIndex];
      if (!currentScene || !sceneVideos[currentScene.id]) {
        return;
      }

      try {
        // Reset video to start when switching to a new scene
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
        }
        // Track when this scene started playing
        sceneStartTimeRef.current = performance.now();
        await videoRef.current?.play();
      } catch (error) {
        logger.error('[FullRecordingPreview] Play error:', error);
      }
    };

    playCurrentScene();
  }, [currentSceneIndex, isPlaying, scenes, sceneVideos, isLoading]);

  // Fallback: Check if video is near the end (onEnded doesn't always fire)
  useEffect(() => {
    if (!isPlaying || !videoRef.current || isTransitioning) return;

    const checkVideoProgress = () => {
      if (!videoRef.current) return;
      
      const currentScene = scenes[currentSceneIndex];
      const expectedDuration = currentScene ? currentScene.duration / 1000 : 0;
      
      // Check if we've reached the end based on our performance timer
      const elapsed = (performance.now() - sceneStartTimeRef.current) / 1000;
      
      // If elapsed time >= scene duration (with small buffer), trigger scene end
      if (elapsed >= expectedDuration - 0.05) {
        handleVideoEnded();
      }
    };

    const intervalId = setInterval(checkVideoProgress, 100); // Check every 100ms

    return () => clearInterval(intervalId);
  }, [isPlaying, currentSceneIndex, scenes, isTransitioning, handleVideoEnded]);

  // Transition animation loop
  useEffect(() => {
    if (!isTransitioning || nextSceneIndex === null) return;
    
    const currentScene = scenes[currentSceneIndex];
    const transitionDuration = (currentScene.transitionOut?.duration || 0) * 1000; // Convert to ms
    
    const animateTransition = () => {
      const elapsed = performance.now() - transitionStartTimeRef.current;
      const progress = Math.min(elapsed / transitionDuration, 1);
      
      setTransitionProgress(progress);
      
      if (progress >= 1) {
        completeTransition();
      } else {
        requestAnimationFrame(animateTransition);
      }
    };
    
    if (transitionDuration > 0) {
      requestAnimationFrame(animateTransition);
    }
  }, [isTransitioning, nextSceneIndex, currentSceneIndex, scenes, completeTransition]);

  // Update current time (continues during transitions for accurate timeline)
  useEffect(() => {
    const updateTime = () => {
      if (isPlaying) {
        if (isTransitioning) {
          // During transition, add transition progress to current scene end time
          const scenesBeforeCurrent = scenes.slice(0, currentSceneIndex);
          const elapsedBefore = scenesBeforeCurrent.reduce((sum, s) => sum + s.duration, 0) / 1000;
          const currentScene = scenes[currentSceneIndex];
          const currentSceneDuration = currentScene ? currentScene.duration / 1000 : 0;
          const transitionDuration = (currentScene?.transitionOut?.duration || 0);
          
          // Time = all previous scenes + current scene duration + transition progress
          setCurrentTime(elapsedBefore + currentSceneDuration + (transitionProgress * transitionDuration));
        } else {
          // Normal playback within a scene
          const scenesBeforeCurrent = scenes.slice(0, currentSceneIndex);
          const elapsedBefore = scenesBeforeCurrent.reduce((sum, s) => sum + s.duration, 0) / 1000;
          
          // Calculate elapsed time in current scene (milliseconds -> seconds)
          const currentSceneElapsed = (performance.now() - sceneStartTimeRef.current) / 1000;
          
          // Cap at current scene's duration to prevent overshoot
          const currentScene = scenes[currentSceneIndex];
          const maxSceneTime = currentScene ? currentScene.duration / 1000 : 0;
          const cappedSceneTime = Math.min(currentSceneElapsed, maxSceneTime);
          
          setCurrentTime(elapsedBefore + cappedSceneTime);
        }
        
        animationFrameRef.current = requestAnimationFrame(updateTime);
      }
    };

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateTime);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, isTransitioning, transitionProgress, currentSceneIndex, scenes]);

  // Fix #3: Wrap in useCallback to prevent stale closures in keyboard shortcuts
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    } else {
      videoRef.current?.play()
        .catch(err => console.error('[FullRecordingPreview] Play error:', err));
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleRestart = useCallback(() => {
    setCurrentSceneIndex(0);
    setCurrentTime(0);
    sceneStartTimeRef.current = performance.now(); // Reset scene start time
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsPlaying(false);
    setCurrentSceneIndex(0);
    setCurrentTime(0);
    if (isFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
    }
    onClose();
  }, [isFullscreen, onClose]);

  // Enhancement #8: Fullscreen toggle handler
  const handleFullscreenToggle = useCallback(() => {
    if (!modalContainerRef.current) return;

    if (!document.fullscreenElement) {
      modalContainerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  }, []);

  // Enhancement #4: Keyboard shortcuts
  // Fix #3: Add handleClose and handlePlayPause to dependencies
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default for keys we handle
      if (['Space', 'KeyR', 'KeyF', 'Escape', 'Digit1', 'Digit2', 'Digit3'].includes(e.code)) {
        e.preventDefault();
      }

      switch (e.code) {
        case 'Space':
          handlePlayPause();
          break;
        case 'KeyR':
          handleRestart();
          break;
        case 'KeyF':
          handleFullscreenToggle();
          break;
        case 'Escape':
          handleClose();
          break;
        case 'Digit1':
        case 'Digit2':
        case 'Digit3':
          const sceneIndex = parseInt(e.code.replace('Digit', '')) - 1;
          if (sceneIndex < scenes.length && scenes[sceneIndex].status === 'recorded') {
            setCurrentSceneIndex(sceneIndex);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, scenes, handlePlayPause, handleRestart, handleClose, handleFullscreenToggle]);

  // Enhancement #6: Apply playback speed to video elements
  // Fix #1: Apply to both current and next video refs
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
    if (nextVideoRef.current) {
      nextVideoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, currentSceneIndex, nextSceneIndex]);

  // Enhancement #9: Progress bar hover handler
  const handleProgressHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || totalDuration === 0) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const time = percent * totalDuration;

    setHoverTime(time);
    setHoverPosition({ x: e.clientX, y: rect.top });
  };

  const handleProgressLeave = () => {
    setHoverTime(null);
    setHoverPosition(null);
  };

  // Enhancement #6: Playback speed menu
  const speedMenuItems = [0.5, 1, 1.5, 2].map(speed => ({
    key: speed.toString(),
    label: `${speed}x`,
    onClick: () => setPlaybackSpeed(speed),
  }));

  const currentScene = scenes[currentSceneIndex];
  const recordedScenes = scenes.filter(s => s.status === 'recorded');
  const progressPercent = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  // Enhancement #1: Calculate scene segments for progress bar
  // Fix #4: Memoize to prevent recalculation on every render
  const sceneSegments = useMemo(() => scenes.map((scene, index) => {
    const start = scenes.slice(0, index).reduce((sum, s) => sum + s.duration, 0) / 1000;
    const end = start + (scene.duration / 1000);
    const duration = scene.duration / 1000;
    const startPercent = totalDuration > 0 ? (start / totalDuration) * 100 : 0;
    const widthPercent = totalDuration > 0 ? (duration / totalDuration) * 100 : 0;
    
    // Calculate fill percentage for this segment
    let fillPercent = 0;
    if (currentTime <= start) {
      fillPercent = 0; // Haven't reached this segment yet
    } else if (currentTime >= end) {
      fillPercent = 100; // Fully completed this segment
    } else {
      // Currently playing within this segment
      fillPercent = ((currentTime - start) / duration) * 100;
    }
    
    return {
      scene,
      startPercent,
      widthPercent,
      fillPercent,
      isActive: index === currentSceneIndex,
      isRecorded: scene.status === 'recorded',
    };
  }), [scenes, totalDuration, currentTime, currentSceneIndex]);

  // Get transition type icon
  const getTransitionIcon = (type?: string) => {
    switch (type) {
      case 'instant': return <Zap size={12} />;
      case 'fade': return <Wind size={12} />;
      case 'crossfade': return <Blend size={12} />;
      case 'slide': return <MoveRight size={12} />;
      default: return <Zap size={12} />;
    }
  };

  // Calculate video opacity based on transition type and progress
  const getVideoOpacity = (position: 'from' | 'to'): number => {
    if (!isTransitioning) return 1;
    
    const currentScene = scenes[currentSceneIndex];
    const transitionType = currentScene.transitionOut?.type || 'cut';
    const progress = transitionProgress;
    
    if (position === 'from') {
      // Current video fading out
      switch (transitionType) {
        case 'cut':
          return progress < 1 ? 1 : 0;
        case 'fade':
          return progress < 0.5 ? 1 : 0;
        case 'crossfade':
          return 1 - progress;
        case 'slide':
          return 1;
        default:
          return 1;
      }
    } else {
      // Next video fading in
      switch (transitionType) {
        case 'cut':
          return progress >= 1 ? 1 : 0;
        case 'fade':
          return progress >= 0.5 ? 1 : 0;
        case 'crossfade':
          return progress;
        case 'slide':
          return 1;
        default:
          return 0;
      }
    }
  };

  // Calculate video transform based on transition type and progress
  const getVideoTransform = (position: 'from' | 'to'): string => {
    if (!isTransitioning) return 'none';
    
    const currentScene = scenes[currentSceneIndex];
    const transitionType = currentScene.transitionOut?.type || 'cut';
    const progress = transitionProgress;
    
    if (transitionType === 'slide') {
      if (position === 'from') {
        return `translateX(-${progress * 100}%)`;
      } else {
        return `translateX(${(1 - progress) * 100}%)`;
      }
    }
    
    return 'none';
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      centered
      destroyOnHidden
      closable={false}
      className={`full-recording-preview-modal ${cinemaMode ? 'cinema' : ''}`}
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className="full-preview-container" ref={modalContainerRef}>
        {/* Header */}
        <div className="preview-header">
          <div className="header-left">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Title level={4} style={{ margin: 0, color: cinemaMode ? '#e8e8e8' : '#262626' }}>
                Full Recording Preview
              </Title>
              {/* Enhancement #10: Export Preview Badge */}
              <div className="export-badge">
                <Text style={{ fontSize: 10, fontWeight: 600, color: '#1890ff' }}>
                  16:9 Preview
                </Text>
              </div>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {recordedScenes.length} scenes • {formatTime(totalDuration)} total
            </Text>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Enhancement #8: Fullscreen Toggle */}
            <Tooltip title="Fullscreen (F)">
              <Button
                type="text"
                icon={<Maximize size={18} />}
                onClick={handleFullscreenToggle}
                className="header-button"
              />
            </Tooltip>
            <Button
              type="text"
              icon={<X size={20} />}
              onClick={handleClose}
              className="close-button"
            />
          </div>
        </div>

        {/* Video Player with Transition Support */}
        <div className="preview-video-container">
          {isLoading ? (
            <div className="loading-state">
              <Text type="secondary">Loading scenes...</Text>
            </div>
          ) : currentScene && sceneVideos[currentScene.id] ? (
            <>
              {/* Current Scene Video */}
              <video
                ref={videoRef}
                src={sceneVideos[currentScene.id]}
                onEnded={handleVideoEnded}
                className="preview-video current-video"
                controls={false}
                style={{
                  opacity: isTransitioning ? getVideoOpacity('from') : 1,
                  transform: isTransitioning ? getVideoTransform('from') : 'none',
                  zIndex: isTransitioning && transitionProgress >= 0.5 ? 1 : 2,
                }}
              />
              
              {/* Next Scene Video (during transition) */}
              {isTransitioning && nextSceneIndex !== null && scenes[nextSceneIndex] && sceneVideos[scenes[nextSceneIndex].id] && (
                <video
                  ref={nextVideoRef}
                  src={sceneVideos[scenes[nextSceneIndex].id]}
                  className="preview-video next-video"
                  controls={false}
                  autoPlay
                  muted={false}
                  style={{
                    opacity: getVideoOpacity('to'),
                    transform: getVideoTransform('to'),
                    zIndex: transitionProgress >= 0.5 ? 2 : 1,
                  }}
                />
              )}
              
              <div className="scene-indicator">
                <Text strong style={{ color: '#fff', fontSize: 12 }}>
                  Scene {currentSceneIndex + 1}/{scenes.length}: {currentScene.title}
                  {isTransitioning && ' → Transitioning...'}
                </Text>
              </div>
            </>
          ) : (
            <div className="no-content-state">
              <Text type="secondary">No recorded scenes to preview</Text>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="preview-controls">
          {/* Enhancement #1 & #9: Segmented Progress Bar with Hover Preview */}
          <div className="progress-section">
            <div className="time-info">
              <Text style={{ fontSize: 11, color: cinemaMode ? '#bfbfbf' : '#595959' }}>
                {formatTime(currentTime)}
              </Text>
              <Text style={{ fontSize: 11, color: cinemaMode ? '#8c8c8c' : '#8c8c8c' }}>
                {formatTime(totalDuration)}
              </Text>
            </div>
            
            <div 
              className="segmented-progress-bar"
              ref={progressBarRef}
              onMouseMove={handleProgressHover}
              onMouseLeave={handleProgressLeave}
            >
              {/* Scene segments */}
              {sceneSegments.map((segment, index) => (
                <div
                  key={segment.scene.id}
                  className={`scene-segment ${segment.isActive ? 'active' : ''} ${!segment.isRecorded ? 'unrecorded' : ''}`}
                  style={{
                    left: `${segment.startPercent}%`,
                    width: `${segment.widthPercent}%`,
                  }}
                >
                  <div className="segment-fill" style={{ width: `${segment.fillPercent}%` }} />
                </div>
              ))}
              
              {/* Progress indicator */}
              <div className="progress-indicator" style={{ left: `${progressPercent}%` }} />
              
              {/* Hover preview tooltip */}
              {hoverTime !== null && hoverPosition && (
                <div 
                  className="hover-preview-tooltip"
                  style={{
                    left: `${(hoverTime / totalDuration) * 100}%`,
                  }}
                >
                  <Text style={{ fontSize: 10, color: '#fff' }}>
                    {formatTime(hoverTime)}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <Space size="middle">
              <Button
                type="primary"
                icon={isPlaying ? <Pause size={16} /> : <Play size={16} />}
                onClick={handlePlayPause}
                disabled={isLoading || recordedScenes.length === 0}
              >
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              
              {/* Enhancement #6: Playback Speed Control */}
              <Dropdown menu={{ items: speedMenuItems, selectedKeys: [playbackSpeed.toString()] }}>
                <Button disabled={isLoading || recordedScenes.length === 0}>
                  {playbackSpeed}x Speed
                </Button>
              </Dropdown>
              
              <Button
                icon={<RotateCcw size={16} />}
                onClick={handleRestart}
                disabled={isLoading || recordedScenes.length === 0}
              >
                Restart
              </Button>
            </Space>
          </div>
        </div>

        {/* Scene List with Enhancements */}
        <div className="preview-scene-list">
          <Text type="secondary" style={{ fontSize: 11, marginBottom: 8, display: 'block' }}>
            Recording Flow:
          </Text>
          <div className="scene-chips-container">
            {scenes.map((scene, index) => (
              <React.Fragment key={scene.id}>
                {/* Enhancement #2, #3, #5: Scene chip with thumbnail, duration, and better styling */}
                <div
                  className={`scene-chip-enhanced ${index === currentSceneIndex ? 'active' : ''} ${scene.status !== 'recorded' ? 'unrecorded' : ''}`}
                  onClick={() => scene.status === 'recorded' && setCurrentSceneIndex(index)}
                >
                  {/* Enhancement #5: Real video thumbnails */}
                  <div className="scene-thumbnail">
                    {scene.status === 'recorded' ? (
                      sceneThumbnails[scene.id] ? (
                        <img 
                          src={sceneThumbnails[scene.id]} 
                          alt={`${scene.title} thumbnail`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="thumbnail-placeholder" style={{ 
                          background: index === currentSceneIndex ? '#1890ff' : '#8c8c8c' 
                        }} />
                      )
                    ) : (
                      <div className="thumbnail-placeholder unrecorded" />
                    )}
                  </div>
                  
                  <div className="scene-info">
                    <Text style={{ fontSize: 11, fontWeight: index === currentSceneIndex ? 600 : 400 }}>
                      {index + 1}. {scene.title}
                    </Text>
                    {/* Enhancement #2: Duration label */}
                    <Text type="secondary" style={{ fontSize: 9 }}>
                      {scene.status === 'recorded' ? formatTime(scene.duration / 1000) : 'Not Recorded'}
                    </Text>
                  </div>
                </div>
                
                {/* Enhancement #7: Transition indicator between scenes */}
                {index < scenes.length - 1 && (
                  <Tooltip title={`${scene.transitionOut?.type || 'instant'} transition`}>
                    <div className="transition-indicator">
                      {getTransitionIcon(scene.transitionOut?.type)}
                    </div>
                  </Tooltip>
                )}
              </React.Fragment>
            ))}
          </div>
          
          {/* Enhancement #4: Keyboard shortcuts hint */}
          <div className="keyboard-shortcuts-hint">
            <Text type="secondary" style={{ fontSize: 9 }}>
              <strong>Shortcuts:</strong> Space: Play/Pause • R: Restart • F: Fullscreen • 1-3: Jump to scene • Esc: Close
            </Text>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FullRecordingPreviewModal;
